import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Upload, Image, FileText, Video, Trash2, Copy, Check, Search, Grid, List, X, Folder, FolderPlus, ChevronRight, Home, Move, Loader2, CheckSquare, Square, Eye, Tag, Users, Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import OptimizedImage from '@/components/OptimizedImage';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'products', label: 'Produits' },
  { value: 'accessories', label: 'Accessoires' },
  { value: 'documents', label: 'Documents' },
  { value: 'banners', label: 'Bannières' },
  { value: 'general', label: 'Général' },
];

export default function MediaLibraryPage() {
  const { user, isAdmin } = useAuth();
  const [media, setMedia] = useState([]);
  const [folders, setFolders] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState({ category: '', type: '', search: '' });
  const [viewMode, setViewMode] = useState('grid');
  const [copiedId, setCopiedId] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(null);
  const [uploadCategory, setUploadCategory] = useState('general');
  const [externalUrl, setExternalUrl] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [showBulkMoveModal, setShowBulkMoveModal] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [showAccessModal, setShowAccessModal] = useState(null);

  // Check if user can delete (admin only)
  const canDelete = isAdmin();
  const canUpload = isAdmin();

  const fetchFolders = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/media/folders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setFolders(data);
    } catch (error) {
      console.error('Failed to fetch folders:', error);
    }
  }, []);

  const fetchMedia = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filter.category) params.append('category', filter.category);
      if (filter.type) params.append('type', filter.type);
      if (currentFolder) params.append('folder_id', currentFolder.id);

      const response = await fetch(`${API_URL}/api/media?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      // Filter by current folder on frontend (in case backend doesn't support it yet)
      const filtered = currentFolder 
        ? data.filter(m => m.folder_id === currentFolder.id)
        : data.filter(m => !m.folder_id);
      setMedia(filtered);
    } catch (error) {
      console.error('Failed to fetch media:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [filter.category, filter.type, currentFolder]);

  useEffect(() => {
    fetchFolders();
    fetchUsers();
  }, [fetchFolders]);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAllUsers(data.filter(u => !u.archived));
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleUpdateAccess = async (itemId, itemType, userIds) => {
    const token = localStorage.getItem('token');
    try {
      const endpoint = itemType === 'folder' 
        ? `${API_URL}/api/media/folders/${itemId}/access`
        : `${API_URL}/api/media/${itemId}/access`;
      
      await fetch(endpoint, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ allowed_users: userIds })
      });
      toast.success('Droits d\'accès mis à jour');
      setShowAccessModal(null);
      fetchMedia();
      fetchFolders();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour des droits');
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/media/folders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newFolderName,
          parent_id: currentFolder?.id || null
        })
      });
      if (!response.ok) throw new Error('Failed to create folder');
      toast.success('Dossier créé');
      setNewFolderName('');
      setShowFolderModal(false);
      fetchFolders();
    } catch (error) {
      toast.error('Erreur lors de la création du dossier');
    }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Supprimer ce dossier ? Les fichiers seront déplacés à la racine.')) return;

    const token = localStorage.getItem('token');
    try {
      await fetch(`${API_URL}/api/media/folders/${folderId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Dossier supprimé');
      if (currentFolder?.id === folderId) setCurrentFolder(null);
      fetchFolders();
      fetchMedia();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleMoveMedia = async (mediaId, targetFolderId) => {
    const token = localStorage.getItem('token');
    try {
      await fetch(`${API_URL}/api/media/${mediaId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ folder_id: targetFolderId || null })
      });
      toast.success('Fichier déplacé');
      setShowMoveModal(null);
      fetchMedia();
    } catch (error) {
      toast.error('Erreur lors du déplacement');
    }
  };

  // Sélection multiple
  const toggleSelectItem = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const selectAll = () => {
    if (selectedItems.length === filteredMedia.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredMedia.map(m => m.id));
    }
  };

  const clearSelection = () => {
    setSelectedItems([]);
  };

  // Suppression en masse
  const handleBulkDelete = async () => {
    if (!window.confirm(`Supprimer ${selectedItems.length} fichier(s) ?`)) return;

    const token = localStorage.getItem('token');
    let successCount = 0;

    for (const mediaId of selectedItems) {
      try {
        await fetch(`${API_URL}/api/media/${mediaId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        });
        successCount++;
      } catch (error) {
        console.error(`Failed to delete ${mediaId}`);
      }
    }

    toast.success(`${successCount} fichier(s) supprimé(s)`);
    setSelectedItems([]);
    fetchMedia();
  };

  // Déplacement en masse
  const handleBulkMove = async (targetFolderId) => {
    const token = localStorage.getItem('token');
    let successCount = 0;

    for (const mediaId of selectedItems) {
      try {
        await fetch(`${API_URL}/api/media/${mediaId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ folder_id: targetFolderId || null })
        });
        successCount++;
      } catch (error) {
        console.error(`Failed to move ${mediaId}`);
      }
    }

    toast.success(`${successCount} fichier(s) déplacé(s)`);
    setSelectedItems([]);
    setShowBulkMoveModal(false);
    fetchMedia();
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const token = localStorage.getItem('token');

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', uploadCategory);
      if (currentFolder) {
        formData.append('folder_id', currentFolder.id);
      }

      try {
        const response = await fetch(`${API_URL}/api/media/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });
        if (!response.ok) throw new Error('Upload failed');
        toast.success(`${file.name} uploadé`);
      } catch (error) {
        toast.error(`Erreur: ${file.name}`);
      }
    }

    setUploading(false);
    setShowUploadModal(false);
    fetchMedia();
  };

  const handleAddExternalUrl = async () => {
    if (!externalUrl) return;

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/media`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          filename: externalUrl.split('/').pop() || 'External Image',
          url: externalUrl,
          type: 'image',
          category: uploadCategory,
          folder_id: currentFolder?.id || null,
          alt_text: ''
        })
      });
      if (!response.ok) throw new Error('Failed to add');
      toast.success('Image ajoutée');
      setExternalUrl('');
      setShowUploadModal(false);
      fetchMedia();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm('Supprimer ce média ?')) return;

    const token = localStorage.getItem('token');
    try {
      await fetch(`${API_URL}/api/media/${mediaId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Média supprimé');
      fetchMedia();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const copyUrl = (url) => {
    const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedId(url);
    toast.success('URL copiée');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  const filteredMedia = media.filter(m => {
    if (filter.search) {
      const search = filter.search.toLowerCase();
      return m.filename?.toLowerCase().includes(search) || 
             m.alt_text?.toLowerCase().includes(search) ||
             m.tags?.some(t => t.toLowerCase().includes(search));
    }
    return true;
  });

  const getTypeIcon = (type) => {
    switch (type) {
      case 'video': return Video;
      case 'document': return FileText;
      default: return Image;
    }
  };

  // Get folders for current level
  const currentLevelFolders = folders.filter(f => 
    currentFolder ? f.parent_id === currentFolder.id : !f.parent_id
  );

  // Build breadcrumb
  const buildBreadcrumb = () => {
    const crumbs = [];
    let folder = currentFolder;
    while (folder) {
      crumbs.unshift(folder);
      folder = folders.find(f => f.id === folder.parent_id);
    }
    return crumbs;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Médiathèque</h1>
          <p className="text-slate-500 text-sm">{media.length} fichiers • {folders.length} dossiers</p>
        </div>
        {canUpload && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFolderModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 font-medium rounded-lg hover:bg-slate-200 transition"
            >
              <FolderPlus className="w-4 h-4" />
              Dossier
            </button>
            <button
              onClick={() => setShowUploadModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white font-medium rounded-lg hover:bg-yellow-600 transition"
            >
              <Plus className="w-4 h-4" />
              Ajouter
          </button>
        </div>
        )}
      </div>

      {/* Breadcrumb */}
      <div className="bg-white rounded-lg shadow-sm p-3 flex items-center gap-2 text-sm">
        <button
          onClick={() => setCurrentFolder(null)}
          className={`flex items-center gap-1 px-2 py-1 rounded hover:bg-slate-100 ${!currentFolder ? 'text-yellow-600 font-medium' : 'text-slate-600'}`}
        >
          <Home className="w-4 h-4" />
          Racine
        </button>
        {buildBreadcrumb().map((folder, index) => (
          <React.Fragment key={folder.id}>
            <ChevronRight className="w-4 h-4 text-slate-400" />
            <button
              onClick={() => setCurrentFolder(folder)}
              className={`px-2 py-1 rounded hover:bg-slate-100 ${currentFolder?.id === folder.id ? 'text-yellow-600 font-medium' : 'text-slate-600'}`}
            >
              {folder.name}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Barre de sélection multiple */}
      {selectedItems.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-yellow-800">
              {selectedItems.length} fichier(s) sélectionné(s)
            </span>
            <button
              onClick={clearSelection}
              className="text-xs text-yellow-600 hover:text-yellow-800 underline"
            >
              Tout désélectionner
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowBulkMoveModal(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-yellow-300 text-yellow-700 text-sm font-medium rounded-lg hover:bg-yellow-100 transition"
            >
              <Move className="w-4 h-4" />
              Déplacer
            </button>
            <button
              onClick={handleBulkDelete}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600 transition"
            >
              <Trash2 className="w-4 h-4" />
              Supprimer
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={filter.search}
                onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
              />
            </div>
          </div>

          <select
            value={filter.category}
            onChange={(e) => setFilter({ ...filter, category: e.target.value })}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
          >
            <option value="">Toutes catégories</option>
            {CATEGORIES.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>

          <select
            value={filter.type}
            onChange={(e) => setFilter({ ...filter, type: e.target.value })}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
          >
            <option value="">Tous types</option>
            <option value="image">Images</option>
            <option value="document">Documents</option>
            <option value="video">Vidéos</option>
          </select>

          <div className="flex items-center gap-1 border border-slate-200 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-slate-100' : ''}`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-slate-100' : ''}`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Folders Grid */}
      {currentLevelFolders.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
          {currentLevelFolders.map(folder => (
            <div
              key={folder.id}
              className="bg-white rounded-lg shadow-sm p-3 cursor-pointer hover:shadow-md transition group relative"
              onClick={() => setCurrentFolder(folder)}
            >
              <div className="flex flex-col items-center">
                <Folder className="w-12 h-12 text-yellow-500 mb-2" />
                <span className="text-sm font-medium text-slate-700 text-center truncate w-full">{folder.name}</span>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.id); }}
                className="absolute top-1 right-1 p-1 bg-red-100 rounded-full opacity-0 group-hover:opacity-100 transition"
              >
                <Trash2 className="w-3 h-3 text-red-500" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Media Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {filteredMedia.map(item => {
            const Icon = getTypeIcon(item.type);
            const isSelected = selectedItems.includes(item.id);
            return (
              <div
                key={item.id}
                className={`bg-white rounded-lg shadow-sm overflow-hidden group relative ${isSelected ? 'ring-2 ring-yellow-500' : ''}`}
              >
                {/* Checkbox de sélection */}
                <button
                  onClick={(e) => { e.stopPropagation(); toggleSelectItem(item.id); }}
                  className={`absolute top-2 left-2 z-10 p-1 rounded ${isSelected ? 'bg-yellow-500 text-white' : 'bg-white/80 text-slate-400 opacity-0 group-hover:opacity-100'} transition`}
                >
                  {isSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                </button>

                <div className="aspect-square bg-slate-100 relative">
                  {item.type === 'image' ? (
                    <img
                      src={getImageUrl(item.url)}
                      alt={item.alt_text || item.filename}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon className="w-12 h-12 text-slate-300" />
                    </div>
                  )}
                  
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-1 flex-wrap p-2">
                    <button
                      onClick={() => setPreviewItem(item)}
                      className="p-2 bg-white rounded-full hover:bg-slate-100"
                      title="Prévisualiser"
                    >
                      <Eye className="w-4 h-4 text-blue-500" />
                    </button>
                    <button
                      onClick={() => copyUrl(item.url)}
                      className="p-2 bg-white rounded-full hover:bg-slate-100"
                      title="Copier l'URL"
                    >
                      {copiedId === item.url ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                    {canDelete && (
                      <>
                        <button
                          onClick={() => setShowMoveModal(item)}
                          className="p-2 bg-white rounded-full hover:bg-slate-100"
                          title="Déplacer"
                        >
                          <Move className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setShowAccessModal(item)}
                          className="p-2 bg-white rounded-full hover:bg-slate-100"
                          title="Droits d'accès"
                        >
                          <Users className="w-4 h-4 text-purple-500" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-2 bg-white rounded-full hover:bg-red-50"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="p-2">
                  <p className="text-xs text-slate-600 truncate" title={item.filename}>
                    {item.filename}
                  </p>
                  {item.category && (
                    <span className="text-[10px] text-slate-400">{item.category}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 w-10">
                  <button onClick={selectAll} className="p-1 hover:bg-slate-200 rounded">
                    {selectedItems.length === filteredMedia.length && filteredMedia.length > 0 ? (
                      <CheckSquare className="w-4 h-4 text-yellow-500" />
                    ) : (
                      <Square className="w-4 h-4 text-slate-400" />
                    )}
                  </button>
                </th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Aperçu</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Nom</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Catégorie</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500">Type</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredMedia.map(item => {
                const Icon = getTypeIcon(item.type);
                const isSelected = selectedItems.includes(item.id);
                return (
                  <tr key={item.id} className={`hover:bg-slate-50 ${isSelected ? 'bg-yellow-50' : ''}`}>
                    <td className="px-4 py-2">
                      <button onClick={() => toggleSelectItem(item.id)} className="p-1 hover:bg-slate-200 rounded">
                        {isSelected ? (
                          <CheckSquare className="w-4 h-4 text-yellow-500" />
                        ) : (
                          <Square className="w-4 h-4 text-slate-400" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-2">
                      <div className="w-12 h-12 bg-slate-100 rounded overflow-hidden">
                        {item.type === 'image' ? (
                          <img src={getImageUrl(item.url)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Icon className="w-6 h-6 text-slate-300" />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <p className="text-sm text-slate-800 truncate max-w-[200px]">{item.filename}</p>
                    </td>
                    <td className="px-4 py-2 text-sm text-slate-500">{item.category || '-'}</td>
                    <td className="px-4 py-2 text-sm text-slate-500">{item.type}</td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => copyUrl(item.url)} className="p-1.5 hover:bg-slate-100 rounded">
                          {copiedId === item.url ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-slate-400" />}
                        </button>
                        <button onClick={() => setShowMoveModal(item)} className="p-1.5 hover:bg-slate-100 rounded">
                          <Move className="w-4 h-4 text-slate-400" />
                        </button>
                        <button onClick={() => handleDelete(item.id)} className="p-1.5 hover:bg-red-50 rounded">
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {filteredMedia.length === 0 && currentLevelFolders.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl">
          <Image className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Ce dossier est vide</p>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Ajouter un média</h2>
              <button onClick={() => setShowUploadModal(false)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {currentFolder && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm">
                  <span className="text-yellow-800">📁 Dossier: <strong>{currentFolder.name}</strong></span>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Catégorie</label>
                <select
                  value={uploadCategory}
                  onChange={(e) => setUploadCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                >
                  {CATEGORIES.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>

              {/* File upload */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Upload fichier</label>
                <label className="block border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-yellow-500 transition">
                  <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                  <span className="text-sm text-slate-500">
                    {uploading ? 'Upload en cours...' : 'Cliquez ou glissez vos fichiers'}
                  </span>
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/*,.pdf,.doc,.docx"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex-1 border-t border-slate-200"></div>
                <span className="text-xs text-slate-400">OU</span>
                <div className="flex-1 border-t border-slate-200"></div>
              </div>

              {/* External URL */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">URL externe</label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={externalUrl}
                    onChange={(e) => setExternalUrl(e.target.value)}
                    placeholder="https://..."
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                  />
                  <button
                    onClick={handleAddExternalUrl}
                    disabled={!externalUrl}
                    className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50"
                  >
                    Ajouter
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Folder Modal */}
      {showFolderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-sm w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Nouveau dossier</h2>
              <button onClick={() => setShowFolderModal(false)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {currentFolder && (
                <p className="text-sm text-slate-500">
                  Dans: <strong>{currentFolder.name}</strong>
                </p>
              )}
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nom du dossier</label>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Ex: Photos produits"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  onKeyPress={(e) => e.key === 'Enter' && handleCreateFolder()}
                />
              </div>

              <button
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim()}
                className="w-full py-2 bg-yellow-500 text-white font-medium rounded-lg hover:bg-yellow-600 disabled:opacity-50"
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move Modal */}
      {showMoveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-sm w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Déplacer vers</h2>
              <button onClick={() => setShowMoveModal(null)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-2 max-h-60 overflow-y-auto">
              <button
                onClick={() => handleMoveMedia(showMoveModal.id, null)}
                className="w-full flex items-center gap-2 p-3 hover:bg-slate-50 rounded-lg text-left"
              >
                <Home className="w-5 h-5 text-slate-400" />
                <span>Racine</span>
              </button>
              
              {folders.map(folder => (
                <button
                  key={folder.id}
                  onClick={() => handleMoveMedia(showMoveModal.id, folder.id)}
                  className={`w-full flex items-center gap-2 p-3 hover:bg-slate-50 rounded-lg text-left ${
                    showMoveModal.folder_id === folder.id ? 'bg-yellow-50 border border-yellow-200' : ''
                  }`}
                >
                  <Folder className="w-5 h-5 text-yellow-500" />
                  <span>{folder.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Bulk Move Modal */}
      {showBulkMoveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-sm w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Déplacer {selectedItems.length} fichier(s) vers</h2>
              <button onClick={() => setShowBulkMoveModal(false)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-2 max-h-60 overflow-y-auto">
              <button
                onClick={() => handleBulkMove(null)}
                className="w-full flex items-center gap-2 p-3 hover:bg-slate-50 rounded-lg text-left"
              >
                <Home className="w-5 h-5 text-slate-400" />
                <span>Racine</span>
              </button>
              
              {folders.map(folder => (
                <button
                  key={folder.id}
                  onClick={() => handleBulkMove(folder.id)}
                  className="w-full flex items-center gap-2 p-3 hover:bg-slate-50 rounded-lg text-left"
                >
                  <Folder className="w-5 h-5 text-yellow-500" />
                  <span>{folder.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewItem && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setPreviewItem(null)}>
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="font-bold truncate">{previewItem.filename}</h2>
              <button onClick={() => setPreviewItem(null)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 flex items-center justify-center bg-slate-100" style={{height: 'calc(90vh - 120px)'}}>
              {previewItem.type === 'image' ? (
                <img 
                  src={getImageUrl(previewItem.url)} 
                  alt={previewItem.filename}
                  className="max-w-full max-h-full object-contain"
                />
              ) : previewItem.type === 'document' && previewItem.filename?.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={getImageUrl(previewItem.url)}
                  className="w-full h-full border-0"
                  title={previewItem.filename}
                />
              ) : (
                <div className="text-center">
                  <FileText className="w-20 h-20 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Aperçu non disponible</p>
                  <a 
                    href={getImageUrl(previewItem.url)} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-orange-500 hover:underline mt-2 inline-block"
                  >
                    Télécharger le fichier
                  </a>
                </div>
              )}
            </div>
            <div className="p-4 border-t flex items-center justify-between bg-slate-50">
              <div className="text-sm text-slate-500">
                Type: {previewItem.type} • Catégorie: {previewItem.category || '-'}
              </div>
              <button 
                onClick={() => copyUrl(previewItem.url)}
                className="px-3 py-1 bg-orange-500 text-white rounded hover:bg-orange-600 text-sm"
              >
                Copier l'URL
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Access Control Modal */}
      {showAccessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold">Droits d'accès</h2>
                <p className="text-sm text-slate-500">{showAccessModal.name || showAccessModal.filename}</p>
              </div>
              <button onClick={() => setShowAccessModal(null)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4 p-3 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                {(!showAccessModal.allowed_users || showAccessModal.allowed_users.length === 0) ? (
                  <>
                    <Unlock className="w-4 h-4 text-green-500" />
                    <span>Accessible à tous</span>
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4 text-orange-500" />
                    <span>Accès restreint à {showAccessModal.allowed_users.length} utilisateur(s)</span>
                  </>
                )}
              </div>
            </div>

            <p className="text-sm text-slate-600 mb-3">Sélectionnez les utilisateurs autorisés (vide = tous) :</p>
            
            <div className="space-y-2 max-h-60 overflow-y-auto border rounded-lg p-2">
              {allUsers.map(u => {
                const isAllowed = showAccessModal.allowed_users?.includes(u.id) || false;
                return (
                  <button
                    key={u.id}
                    onClick={() => {
                      const currentAllowed = showAccessModal.allowed_users || [];
                      const newAllowed = isAllowed
                        ? currentAllowed.filter(id => id !== u.id)
                        : [...currentAllowed, u.id];
                      setShowAccessModal({...showAccessModal, allowed_users: newAllowed});
                    }}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg text-left transition ${
                      isAllowed ? 'bg-orange-50 border border-orange-200' : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded flex items-center justify-center ${
                      isAllowed ? 'bg-orange-500 text-white' : 'border-2 border-slate-300'
                    }`}>
                      {isAllowed && <Check className="w-3 h-3" />}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{u.name}</p>
                      <p className="text-xs text-slate-500">{u.role}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => {
                  setShowAccessModal({...showAccessModal, allowed_users: []});
                }}
                className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Rendre public
              </button>
              <button
                onClick={() => handleUpdateAccess(
                  showAccessModal.id, 
                  showAccessModal.name ? 'folder' : 'file',
                  showAccessModal.allowed_users || []
                )}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
